import {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Button} from '@/components/app/button';
import {Badge} from '@/components/ui/badge';
import {confirm} from '@/components/app/confirmation';
import {Modal} from '@/components/app/modal';
import {NumberInput} from '@/components/app/inputs';
import {LoadingSkeleton} from '@/components/app/feedback';
import {StatusBadge} from '@/components/app/feedback';
import {ContentHints} from '@/components/app/hint';
import {cn} from '@/lib/utils';
import groupBy from 'lodash/groupBy';
import type {Step} from 'intro.js-react';
import {Icon} from '../../../app/components/Icon';
import {InfoSection} from '../../../app/components/InfoSection';
import {Steps} from '../../../app/components/Intro';
import {useGetBiobookCountsQuery} from '../../../app/services/biobookApi';
import {useLazyGetSubConditionsQuery} from '../../../app/services/conditionApi';
import {MenuButton} from '../../../app/components/MenuButton';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {
  useCreateBiotwinConditionMutation,
  useDeleteBiotwinConditionMutation,
  useGetBiotwinConditionsQuery,
  useGetBiotwinQuery,
  useGetBiotwinScoresQuery,
  useGetBiotwinsQuery,
  useGetRelevantConditionsQuery,
  useUpdateBiotwinConditionMutation,
  useUpdateBiotwinMutation,
  useUpdateBulkBiotwinConditionsMutation,
} from '../../../app/services/biotwinApi';
import {toastError, toastSuccess} from '../../../app/toast';
import {selectTenantId} from '../../auth/authSlice';
import {useSessionState} from '../../auth/useSessionState';
import {useAppEventListen} from '../../core/hooks/useAppEventListen';
import {useBlockUI} from '../../core/hooks/useBlockUI';
import {PersonContext} from '../../person/contexts/PersonContext';
import {useDemo} from '../../demo/hooks/useDemo';
import {isApiResponseBodyError, isFetchBaseQueryError} from '../../../app/utils/apiUtils';
import {useAssistantUtils} from '../../core/hooks/useAssistantUtils';
import {useSession} from '../../auth/hooks/useSession';
import {CategoryArrow} from '../components/CategoryArrow';
import {BiotwinBasicsViewer} from '../components/BiotwinBasicsViewer';
import {ConditionSearchBar} from '../components/ConditionSearchBar';
import {BiotwinWizard} from '../components/BiotwinWizard';
import {BiotwinBasicsForm} from '../components/BiotwinBasicsForm';
import {NoteForm} from '../components/NoteForm';
import {LeafConditionSelector} from '../components/LeafConditionSelector';
import {useReadOnly} from '../hooks/useReadOnly';
import {BiotwinCondition, Condition, ConditionRelationType, ConditionTypeName, RelevantCondition} from '../types';
import {
  compareRelevantConditions,
  compareRelevantConditionsByType,
  getConditionTypeColorByName,
  getPatientSettingRelevantConditions,
  getRequiredRelatedRelevantConditions,
  getSelectedRelevantConditions,
} from '../utils';
import styles from './Biotwin.module.css';
import {BiotwinActions} from '../components/BiotwinActions';
import {ConditionSelectionPanel} from '../components/ConditionSelectionPanel';
import {RelatedConditionsPanel} from '../components/RelatedConditionsPanel';
import {SettingSelectionPanel} from '../components/SettingSelectionPanel';
import {isCompleteBiotwin} from '../helpers/utils';
import {useGetTenantPatientInfoQuery, useUpdateTenantPatientInfoMutation} from '../../../app/services/tenantApi';
import {IntegratedBiotwinPreviewTable} from '../components/IntegratedBiotwinPreviewTable';
import {confirmDialogAsync} from '../../../app/confirmDialogAsync';
import {Form} from '../../../app/components/Form';
import {DateField} from '../../core/components/DateField';
const settingsHeaderRowId = 'header-settings';
type ConditionRow = {
  groupRowId?: string | null;
  headerRowId: string;
  hidden?: boolean;
  id: string;
  relevantCondition: RelevantCondition;
  type: 'condition';
};
type ConditionSelectionRow = {
  conditionTypeName: string;
  hidden?: boolean;
  id: string;
  type: 'condition-selection';
};
type DetailsRow = {
  hidden?: boolean;
  id: string;
  relevantCondition: RelevantCondition;
  type: 'details';
};
type HeaderRow = {
  conditionTypeName: string;
  hidden?: boolean;
  id: string;
  type: 'header';
};
type SettingSelectionRow = {
  hidden?: boolean;
  id: string;
  relevantCondition: RelevantCondition;
  type: 'setting-selection';
};
type Row = ConditionRow | ConditionSelectionRow | DetailsRow | HeaderRow | SettingSelectionRow;
export const Biotwin = () => {
  const session = useSession();
  const {hasRelation, relationsByConditionId, relevantConditionsDeduplicated, userId} = useContext(PersonContext);
  const {data: biotwin, isLoading: isLoadingGetBiotwin} = useGetBiotwinQuery({
    id: userId,
  });
  const {
    data: biotwinConditions,
    isFetching: isFetchingGetBiotwinConditions,
    isLoading: isLoadingGetBiotwinConditions,
  } = useGetBiotwinConditionsQuery({
    userId,
  });
  const [createBiotwinCondition, {isLoading: isLoadingCreateBiotwinCondition}] = useCreateBiotwinConditionMutation();
  const [deleteBiotwinCondition, {isLoading: isLoadingDeleteBiotwinCondition}] = useDeleteBiotwinConditionMutation();
  const [updateBulkBiotwinCondition, {isLoading: isLoadingUpdateBulkBiotwinCondition}] =
    useUpdateBulkBiotwinConditionsMutation();
  const [introDisplayed, setIntroDisplayed] = useSessionState('intro.conditions.displayed');
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const [introNewConditionDisplayed, setIntroNewConditionDisplayed] = useSessionState('intro.newCondition.displayed');
  const [stepsNewConditionEnabled, setStepsNewConditionEnabled] = useState(false);
  const {isReadOnly} = useReadOnly();
  const readOnly = isReadOnly(biotwin);
  const {isDemo, showLimitReachedDialog} = useDemo();
  const {pushAssistantContent} = useAssistantUtils();
  const tenantId = useAppSelector(selectTenantId);
  const {data: patientInfo, isFetching: isFetchingGetPatientInfo} = useGetTenantPatientInfoQuery(
    {
      patientId: biotwin?.patientId ?? '',
      tenantId,
    },
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: !isCompleteBiotwin(biotwin) || biotwin?.patientId == null || tenantId == null,
    },
  );
  const [updateTenantPatientInfo, {isLoading: isLoadingUpdateTenantPatientInfo}] = useUpdateTenantPatientInfoMutation();
  const [integratedBiotwinPreviewDialogVisible, setIntegratedBiotwinPreviewDialogVisible] = useState(false);
  const {
    data: {items: biotwins} = {
      items: [],
    },
  } = useGetBiotwinsQuery(
    {},
    {
      skip: session?.isDemo !== 'true',
    },
  );
  const navigate = useNavigate();
  const {data: biobookCounts, isFetching: isFetchingGetBiobookCounts} = useGetBiobookCountsQuery({
    userId,
  });
  const {data: biotwinScores} = useGetBiotwinScoresQuery({
    userId,
  });
  const {isLoading: isLoadingGetRelevantConditions} = useGetRelevantConditionsQuery({
    userId,
  });
  const [getSubConditions, {isFetching: isFetchingGetSubConditions}] = useLazyGetSubConditionsQuery();
  const [updateBiotwin, {isLoading: isLoadingUpdateBiotwin}] = useUpdateBiotwinMutation();
  const [updateBiotwinCondition, {isLoading: isLoadingUpdateBiotwinCondition}] = useUpdateBiotwinConditionMutation();
  const patientSettingRelevantConditions = getPatientSettingRelevantConditions(relevantConditionsDeduplicated ?? []);
  const selectedRelevantConditions = getSelectedRelevantConditions(relevantConditionsDeduplicated ?? []);
  const requiredRelatedRelevantConditions = getRequiredRelatedRelevantConditions(relevantConditionsDeduplicated ?? []);
  const rootRelevantConditions = [
    ...patientSettingRelevantConditions,
    ...selectedRelevantConditions.filter((relevantCondition) => !relevantCondition.isPatientSetting),
    ...requiredRelatedRelevantConditions,
  ];
  const [biotwinBasicsFormDialogVisible, setBiotwinBasicsFormDialogVisible] = useState(false);
  const [biotwinWizardVisible, setBiotwinWizardVisible] = useState(false);
  const [demoBiotwinInfoDialogVisible, setDemoBiotwinInfoDialogVisible] = useState(false);
  const [activeRelevantCondition, setActiveRelevantCondition] = useState<RelevantCondition>();
  const [noteFormDialogVisible, setNoteFormDialogVisible] = useState(false);
  const [dateFormDialogVisible, setDateFormDialogVisible] = useState(false);
  const activeBiotwinCondition = biotwinConditions?.find(
    ({conditionId}) => conditionId === activeRelevantCondition?.conditionId,
  );
  const [leafConditionSelectorDialogState, setLeafConditionSelectorDialogState] = useState<
    | {
        visible: false;
      }
    | {
        condition: Condition;
        optional: boolean;
        visible: true;
      }
  >({
    visible: false,
  });
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const lastRelevantConditions = useRef(relevantConditionsDeduplicated);
  const newRelevantConditions = useRef<RelevantCondition[]>([]);
  const rows = rowsFactory();
  const container = useRef<HTMLDivElement>(null);
  const careSettingRelevantCondition = useMemo(
    () => relevantConditionsDeduplicated?.find((rc) => /care-setting/i.test(rc.conditionName)),
    [relevantConditionsDeduplicated],
  );
  const [similarityThreshold, setSimilarityThreshold] = useState(40);
  if (lastRelevantConditions.current !== relevantConditionsDeduplicated) {
    if (!!lastRelevantConditions.current?.length && !!relevantConditionsDeduplicated?.length) {
      newRelevantConditions.current = relevantConditionsDeduplicated.filter(
        (relevantCondition) =>
          !lastRelevantConditions.current?.some(
            (lastRelevantCondition) => lastRelevantCondition.conditionId === relevantCondition.conditionId,
          ),
      );
    }
    lastRelevantConditions.current = relevantConditionsDeduplicated;
  }
  useEffect(() => {
    if (patientInfo != null && !patientInfo.isAllProcessed) {
      setIntegratedBiotwinPreviewDialogVisible(true);
    }
  }, [patientInfo]);
  useBlockUI(
    isFetchingGetSubConditions ||
      isFetchingGetPatientInfo ||
      isLoadingCreateBiotwinCondition ||
      isLoadingDeleteBiotwinCondition ||
      isLoadingUpdateBiotwin ||
      isLoadingUpdateBiotwinCondition ||
      isLoadingUpdateBulkBiotwinCondition ||
      isLoadingUpdateTenantPatientInfo,
  );
  useAppEventListen(
    'help',
    useCallback(() => {
      if (!isDemo && isCompleteBiotwin(biotwin)) {
        setStepsEnabled(true);
      }
    }, [biotwin, isDemo]),
  );
  useEffect(() => {
    if (isDemo) {
      return pushAssistantContent({
        detail: 'The more detailed biotwin, gives you more accurate biobook.',
        detailDelay: 1000,
        summary: 'This is a Biotwin.',
      });
    }
  }, [isDemo]);
  useLayoutEffect(() => {
    if (expandedRowIds.length > 0 && container.current) {
      container.current?.querySelector(`[data-row-id="${expandedRowIds[0]}"]`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [expandedRowIds]);
  if (
    !isDemo &&
    !biotwin?.isSample &&
    isCompleteBiotwin(biotwin) &&
    !isFetchingGetPatientInfo &&
    !isFetchingGetBiotwinConditions &&
    !introDisplayed &&
    !stepsEnabled
  ) {
    setStepsEnabled(true);
    return null;
  }
  if (
    !biotwin?.isSample &&
    isCompleteBiotwin(biotwin) &&
    introDisplayed &&
    !introNewConditionDisplayed &&
    relevantConditionsDeduplicated?.some((rc) => rc.conditionRelationType === ConditionRelationType.Selected) &&
    !stepsNewConditionEnabled
  ) {
    setStepsNewConditionEnabled(true);
    return null;
  }
  return (
    <div
      className={cn(styles.biotwinPage, 'min-h-full sm:p-6', {
        'flex flex-col': !isLoadingGetRelevantConditions && rootRelevantConditions.length === 0,
      })}
      ref={container}
    >
      <div className="flex items-center gap-2 m-4 sm:m-0 sm:mb-4">
        <div
          className="flex flex-wrap items-center gap-2 max-w-full whitespace-nowrap"
          style={{
            width: '35rem',
          }}
        >
          <BiotwinBasicsViewer
            className="flex-1 flex flex-wrap items-center gap-2 whitespace-nowrap"
            isLoading={isLoadingGetBiotwin}
            tagClassName="min-w-0 flex-1"
            value={biotwin}
          />
          {!readOnly && (
            <div
              className="flex items-center"
              style={{
                height: 0,
              }}
            >
              <Button
                className={cn(
                  cn({
                    'opacity-0': isLoadingGetBiotwin,
                  }),
                  'rounded-full',
                )}
                disabled={isLoadingGetBiotwin}
                onClick={() => setBiotwinBasicsFormDialogVisible(true)}
                hint="Edit Basics"
                variant="text"
                type="button"
                startIcon={<Icon icon={'pencil'} />}
                aria-label={'Edit'}
                size="icon"
              />
            </div>
          )}
        </div>
        <div className={'flex-1'} />
        {session?.isAdmin === 'true' && (
          <NumberInput
            onValueChange={(e) => setSimilarityThreshold(e ?? 0)}
            inputClassName={'w-[6rem]'}
            max={100}
            min={0}
            showButtons
            step={1}
            value={similarityThreshold}
          />
        )}
        {!readOnly && patientInfo != null && !patientInfo.isAllProcessed && (
          <Button
            onClick={() => setIntegratedBiotwinPreviewDialogVisible(true)}
            hint={'New data from HIS, please review.'}
            type={'button'}
            variant="warning-ghost"
            className="rounded-full"
            startIcon={<Icon icon={'exclamation-triangle'} />}
            aria-label={'exclamation triangle'}
            size="icon"
          />
        )}
        {!readOnly && <BiotwinActions />}
      </div>

      {!readOnly && isLoadingGetBiotwinConditions && (
        <div className="flex mb-4 sm:mb-6">
          <LoadingSkeleton height="2.8125rem" shape="rectangle" width="100%" />
        </div>
      )}

      {!readOnly && !isLoadingGetBiotwinConditions && (
        <ConditionSearchBar
          className="mb-4 sm:mb-6"
          fieldClassName="mb-0"
          onSelect={({value}) => {
            if (isDemo && biotwin?.isDemo) {
              setDemoBiotwinInfoDialogVisible(true);
            } else if (!readOnly) {
              addCondition(value, true);
              setExpandedRowIds([]);
            }
          }}
          similarityThreshold={similarityThreshold / 100}
          userId={userId}
        />
      )}

      {isLoadingGetRelevantConditions && (
        <div className="flex flex-col gap-2 p-4 sm:px-0 sm:py-2">
          <LoadingSkeleton height="2rem" shape="rectangle" width="10rem" />
          <div className="flex items-center gap-2">
            <LoadingSkeleton height="2rem" shape="rectangle" width="60%" />
            <LoadingSkeleton height="2rem" shape="rectangle" width="20%" />
            <LoadingSkeleton height="2rem" shape="rectangle" width="20%" />
          </div>
          <div className="flex items-center gap-2">
            <LoadingSkeleton height="2rem" shape="rectangle" width="60%" />
            <LoadingSkeleton height="2rem" shape="rectangle" width="20%" />
            <LoadingSkeleton height="2rem" shape="rectangle" width="20%" />
          </div>
          <div className="flex items-center gap-2">
            <LoadingSkeleton height="2rem" shape="rectangle" width="60%" />
            <LoadingSkeleton height="2rem" shape="rectangle" width="20%" />
            <LoadingSkeleton height="2rem" shape="rectangle" width="20%" />
          </div>
        </div>
      )}

      {!isLoadingGetRelevantConditions && rootRelevantConditions.length === 0 && (
        <InfoSection
          className="flex-1"
          icon="info-circle"
          message="Search features to add this biotwin."
          title="No Features"
        />
      )}

      {!isLoadingGetRelevantConditions &&
        rootRelevantConditions.length > 0 &&
        rows.filter((row) => !row.hidden).map((row) => renderRow(row))}

      <BiotwinWizard
        noNewBiotwin
        noViewBiotwin
        onCancel={() => setBiotwinWizardVisible(false)}
        onFinish={({biotwin}) => {
          setBiotwinWizardVisible(false);
          navigate(`/biotwins/${biotwin.id}`);
        }}
        tenantId={tenantId}
        visible={biotwinWizardVisible}
      />

      <Modal
        closeOnEscape={false}
        contentClassName={'flex flex-col overflow-visible'}
        contentStyle={{
          height: '35rem',
        }}
        title={`${biotwin?.name}'s Integration`}
        onClose={() => setIntegratedBiotwinPreviewDialogVisible(false)}
        style={{
          maxWidth: 'calc(100% - 3rem)',
          width: '1280px',
        }}
        open={integratedBiotwinPreviewDialogVisible}
      >
        <IntegratedBiotwinPreviewTable
          biotwinId={userId}
          className={'-mt-4'}
          onClose={async () => {
            const confirmed = await confirmDialogAsync({
              className: 'w-[30rem]',
              dismissible: false,
              message: 'Are you sure? When you close it this way, this data will not be brought to you again.',
              title: 'Confirm',
              confirmClassName: 'rounded-full',
              confirmLabel: 'OK',
              cancelClassName: 'rounded-full bg-transparent text-muted-foreground',
              cancelLabel: 'Cancel',
            });
            if (confirmed) {
              await updateTenantPatientInfo({
                data: true,
                patientId: biotwin?.patientId ?? '',
                tenantId,
              });
              setIntegratedBiotwinPreviewDialogVisible(false);
            }
          }}
          onSave={async ({items}) => {
            if (items.length === 0) {
              await confirmDialogAsync({
                dismissible: false,
                message: 'Please select at least 1 item from HIS data to integrate.',
                title: 'No Data Selected',
                confirmClassName: 'rounded-full',
                confirmLabel: 'OK',
                cancelClassName: 'hidden',
                showCancel: false,
              });
              return;
            }
            await updateBulkBiotwinCondition({
              added: items.map((item) => ({
                conditionId: item.condition!.id,
                date: item.date,
                drugId: item.condition!.drugId,
              })),
              removedIds: [],
              userId,
            }).unwrap();
            await updateTenantPatientInfo({
              data: true,
              patientId: biotwin?.patientId ?? '',
              tenantId,
            });
            setIntegratedBiotwinPreviewDialogVisible(false);
          }}
        />
      </Modal>

      <Modal
        className={'w-[30rem]'}
        footer={
          <>
            {biotwins.every((b) => b.isDemo) && (
              <Button
                onClick={() => {
                  setDemoBiotwinInfoDialogVisible(false);
                  setBiotwinWizardVisible(true);
                }}
                className="rounded-full"
                type="button"
              >
                {'Create new biotwin'}
              </Button>
            )}
            <Button
              className={cn('text-muted-foreground', 'rounded-full')}
              onClick={() => setDemoBiotwinInfoDialogVisible(false)}
              variant="ghost"
              type="button"
            >
              {'Close'}
            </Button>
          </>
        }
        title={'Demo'}
        onClose={() => setDemoBiotwinInfoDialogVisible(false)}
        open={demoBiotwinInfoDialogVisible}
      >
        This is a readonly biotwin for demo purposes. Please create a new biotwin to add features.
      </Modal>

      <Modal
        className="w-[30rem]"
        title={`${biotwin?.name}'s Basics`}
        onClose={() => setBiotwinBasicsFormDialogVisible(false)}
        open={biotwinBasicsFormDialogVisible}
      >
        <BiotwinBasicsForm
          defaultValues={{
            ...biotwin,
            heightUnit: biotwin?.heightUnit ?? 'cm',
            weightUnit: biotwin?.weightUnit ?? 'kg',
          }}
          onSubmit={async (fieldValues) => {
            if (!biotwin) {
              return;
            }
            try {
              await updateBiotwin({
                id: biotwin.id,
                changes: {
                  ...fieldValues,
                  name: undefined,
                },
              }).unwrap();
              toastSuccess('Biotwin is updated successfully.');
            } catch (e) {
              toastError(e);
            } finally {
              setExpandedRowIds([]);
              setBiotwinBasicsFormDialogVisible(false);
            }
          }}
        />
      </Modal>

      <Modal
        className="w-[30rem]"
        title={activeBiotwinCondition?.note ? 'Edit Note' : 'Add Note'}
        onClose={() => setNoteFormDialogVisible(false)}
        open={noteFormDialogVisible}
      >
        <div className={'mb-6 text-center text-blue-400'}>{activeRelevantCondition?.conditionName}</div>
        <NoteForm
          defaultValues={{
            text: activeBiotwinCondition?.note ?? '',
          }}
          onSubmit={async ({text}) => {
            if (!activeBiotwinCondition) {
              return;
            }
            try {
              await updateBiotwinCondition({
                ...activeBiotwinCondition,
                note: text,
                userId,
              }).unwrap();
              toastSuccess(`Note is ${activeBiotwinCondition?.note ? 'updated' : 'added'} successfully.`);
            } catch (e) {
              toastError(e);
            } finally {
              setNoteFormDialogVisible(false);
            }
          }}
        />
      </Modal>

      <Modal
        className="w-[30rem]"
        title={activeBiotwinCondition?.date ? 'Edit Date' : 'Add Date'}
        onClose={() => setDateFormDialogVisible(false)}
        open={dateFormDialogVisible}
      >
        <div className={'mb-6 text-center text-blue-400'}>{activeRelevantCondition?.conditionName}</div>
        <div className={'mb-6 text-center text-blue-400'}>
          You can enter the date for the feature as year or month and year or full date.
        </div>
        <Form
          defaultValues={{
            date: activeBiotwinCondition?.date,
          }}
          fields={[
            {
              label: 'Date',
              labelClassName: 'sr-only',
              name: 'date',
              type: DateField,
            },
          ]}
          onSubmit={async ({date}) => {
            if (!activeBiotwinCondition) {
              return;
            }
            try {
              await updateBiotwinCondition({
                ...activeBiotwinCondition,
                date,
                userId,
              }).unwrap();
              toastSuccess(`Date is ${activeBiotwinCondition?.date ? 'updated' : 'added'} successfully.`);
            } catch (e) {
              toastError(e);
            } finally {
              setDateFormDialogVisible(false);
            }
          }}
          submitButton={{
            children: activeBiotwinCondition?.date ? 'Update Date' : 'Add Date',
            variant: 'warning',
          }}
        />
      </Modal>

      <Modal
        title="Let's Get More Specific"
        onClose={() =>
          setLeafConditionSelectorDialogState({
            visible: false,
          })
        }
        style={{
          maxWidth: 'calc(100% - 3rem)',
          width: '50rem',
        }}
        open={leafConditionSelectorDialogState.visible}
      >
        {leafConditionSelectorDialogState.visible && (
          <LeafConditionSelector
            condition={leafConditionSelectorDialogState.condition}
            onSelect={(e) => {
              void onAddCondition({
                conditionId: e.condition.id,
                drugId: e.condition.drugId ?? undefined,
              });
              setLeafConditionSelectorDialogState({
                visible: false,
              });
            }}
            optional={leafConditionSelectorDialogState.optional}
          />
        )}
      </Modal>

      <Steps
        enabled={stepsEnabled}
        initialStep={0}
        steps={steps}
        onExit={() => {
          document.body.classList.remove('introjs-steps-enabled');
          if (stepsEnabled) {
            setStepsEnabled(false);
            if (!introDisplayed) {
              setIntroDisplayed(true);
            }
            if (
              relevantConditionsDeduplicated?.some((rc) => rc.conditionRelationType === ConditionRelationType.Selected)
            ) {
              setStepsNewConditionEnabled(true);
            }
          }
        }}
        onStart={() => document.body.classList.add('introjs-steps-enabled')}
        options={{
          hideNext: false,
          overlayOpacity: 1,
        }}
      />

      <Steps
        enabled={stepsNewConditionEnabled}
        initialStep={0}
        steps={stepsNewConditionFactory()}
        onExit={() => {
          document.body.classList.remove('introjs-steps-enabled');
          if (stepsNewConditionEnabled) {
            setStepsNewConditionEnabled(false);
            if (!introNewConditionDisplayed) {
              setIntroNewConditionDisplayed(true);
            }
          }
        }}
        onStart={() => document.body.classList.add('introjs-steps-enabled')}
        options={{
          hideNext: false,
          overlayOpacity: 1,
        }}
      />
    </div>
  );
  function renderRow(row: Row) {
    switch (row.type) {
      case 'condition':
        return renderConditionRow(row);
      case 'condition-selection':
        return renderConditionSelectionRow(row);
      case 'details':
        return renderDetailsRow(row);
      case 'header':
        return renderHeaderRow(row);
      case 'setting-selection':
        return renderSettingSelectionRow(row);
      default:
        return null;
    }
  }
  function renderConditionRow(row: ConditionRow) {
    const index = rows.indexOf(row);
    const nextRow = rows[index + 1];
    const relevantCondition = row.relevantCondition;
    const relations = relationsByConditionId[relevantCondition.conditionId];
    // const othersVisible = session?.isAdmin === 'true' && relations.suggestedList.length > 0;
    const expanderVisible =
      relevantCondition.conditionId >= 0 && relations.optionalRelatedList.length > 0 && row.groupRowId == null;
    const assigned = relevantCondition.conditionRelationType === ConditionRelationType.RequiredRelated;
    const expanded = expandedRowIds.includes(row.id);
    const phraseCount =
      biobookCounts?.conditionPhraseCounts.find(({conditionId}) => conditionId === relevantCondition.conditionId)
        ?.phraseCount ?? 0;
    const biotwinCondition = biotwinConditions?.find(({conditionId}) => conditionId === relevantCondition.conditionId);
    const note = biotwinCondition?.note;
    const date = biotwinCondition?.date;
    const relatedConditionIds =
      relevantCondition.relatedConditionIds ??
      (relevantCondition.relatedConditionId == null ? [] : [relevantCondition.relatedConditionId]);
    const relatedScoreIds = relevantCondition.relatedScoreIds ?? [];
    return (
      <div
        className={cn(
          'border-zinc-300',
          nextRow != null && nextRow.type !== 'header' && 'border-b',
          newRelevantConditions.current.includes(relevantCondition) && styles.blink,
        )}
        data-row-id={row.id}
        key={row.id}
        style={{
          scrollMarginTop: '2.25rem',
        }}
      >
        <div
          className={cn(
            'flex items-center py-1',
            assigned && 'bg-gray-50',
            expanded && !assigned && 'bg-white sticky z-1',
          )}
          style={
            expanded
              ? {
                  top: '2.25rem',
                }
              : undefined
          }
        >
          <div className={'flex w-12 h-11.25 shrink-0 items-center justify-center'}>
            {expanderVisible && (
              <Button
                data-name={'biotwin-condition-expander'}
                onClick={() =>
                  setExpandedRowIds(expanded ? expandedRowIds.filter((rowId) => rowId !== row.id) : [row.id])
                }
                variant="text"
                className="rounded-full"
                type="button"
                startIcon={<Icon icon={expanded ? 'chevron-down' : 'chevron-right'} />}
                aria-label={'More actions'}
                size="icon"
              />
            )}
          </div>

          <div className="flex items-center gap-4 flex-1 mr-4">
            {row.groupRowId != null && (
              <CategoryArrow
                style={{
                  marginTop: '-.75rem',
                }}
              />
            )}
            {relevantCondition.conditionName}
            <Link
              className={cn({
                'pointer-events-none': phraseCount === 0,
              })}
              data-name={'biotwin-condition-phrase-count-link'}
              state={{
                conditionIds: [relevantCondition.conditionId],
              }}
              to="../biobook"
            >
              <Badge
                variant={phraseCount === 0 ? 'secondary' : 'default'}
                className="inline-flex items-center justify-center rounded-3xl px-2"
              >
                {isFetchingGetBiobookCounts ? <Icon icon="spinner" spin /> : phraseCount}
              </Badge>
            </Link>
          </div>

          {relevantCondition.hideFromBiotype && (
            <>
              <StatusBadge
                className="bg-transparent text-foreground font-normal p-0 mr-4"
                icon="eye-slash"
                id={`biotwin-condition-${relevantCondition.conditionId}-hide-from-biotwin-tag`}
              >
                {'Hidden'}
              </StatusBadge>
              <ContentHints
                side={'left'}
                target={`#biotwin-condition-${relevantCondition.conditionId}-hide-from-biotwin-tag`}
              >
                This feature is hidden from the biotwin.
              </ContentHints>
            </>
          )}

          {relevantCondition.drugName != null && (
            <>
              <StatusBadge
                className="bg-transparent text-foreground font-normal p-0 mr-4"
                icon="barcode"
                id={`biotwin-condition-${relevantCondition.conditionId}-drug-name-tag`}
              >
                {relevantCondition.drugName}
              </StatusBadge>
              <ContentHints side={'left'} target={`#biotwin-condition-${relevantCondition.conditionId}-drug-name-tag`}>
                Brand name
              </ContentHints>
            </>
          )}

          {note != null && (
            <>
              <StatusBadge
                className="bg-transparent text-foreground font-normal p-0 mr-4"
                icon="file"
                id={`biotwin-condition-${relevantCondition.conditionId}-note-tag`}
              >
                {'Note'}
              </StatusBadge>
              <ContentHints side={'left'} target={`#biotwin-condition-${relevantCondition.conditionId}-note-tag`}>
                {note}
              </ContentHints>
            </>
          )}

          {date != null && (
            <StatusBadge
              className="bg-transparent text-foreground font-normal p-0 mr-4"
              icon="calendar"
              id={`biotwin-condition-${relevantCondition.conditionId}-date-tag`}
            >
              {new Date(date).toLocaleDateString('en', {
                month: 'numeric',
                year: 'numeric',
              })}
            </StatusBadge>
          )}

          {assigned && (
            <>
              <StatusBadge
                className="bg-transparent text-foreground font-normal p-0 mr-4"
                icon="tag"
                id={`biotwin-condition-${relevantCondition.conditionId}-assigned-tag`}
              >
                {'Assigned'}
              </StatusBadge>
              {(relatedConditionIds.length > 0 || relatedScoreIds.length > 0) && (
                <ContentHints side={'left'} target={`#biotwin-condition-${relevantCondition.conditionId}-assigned-tag`}>
                  Secondary To:
                  <ul>
                    {relatedConditionIds.map((conditionId) => {
                      const rc = relevantConditionsDeduplicated?.find((rc) => rc.conditionId === conditionId);
                      return <li key={conditionId}>{rc?.conditionName ?? conditionId}</li>;
                    })}
                    {relatedScoreIds.map((scoreId) => {
                      const score = biotwinScores?.find((score) => score.scoreId === scoreId);
                      return (
                        <li key={scoreId}>
                          {score?.scoreShortDescription ?? scoreId} {score?.scoreValue} {score?.scoreValueUnit}
                        </li>
                      );
                    })}
                  </ul>
                </ContentHints>
              )}
            </>
          )}

          {!readOnly && (
            <MenuButton
              data-name={'biotwin-condition-operations'}
              actions={[
                {
                  label: note ? 'Edit Note' : 'Add Note',
                  icon: 'file',
                  onSelect: () => {
                    setActiveRelevantCondition(relevantCondition);
                    setNoteFormDialogVisible(true);
                  },
                },
                {
                  disabled: assigned || relevantCondition.isPatientSetting,
                  label: date ? 'Edit Date' : 'Add Date',
                  icon: 'calendar',
                  onSelect: () => {
                    setActiveRelevantCondition(relevantCondition);
                    setDateFormDialogVisible(true);
                  },
                },
                {
                  disabled: assigned || relevantCondition.isPatientSetting,
                  label: 'Remove',
                  icon: 'trash',
                  onSelect: () => {
                    onRemoveCondition?.({
                      conditionId: relevantCondition.conditionId,
                    });
                  },
                },
              ]}
              variant="text"
              className="rounded-full"
              type="button"
              startIcon={<Icon icon={'ellipsis-h'} />}
              aria-label={'More actions'}
              size="icon"
            />
          )}
        </div>
      </div>
    );
  }
  function renderConditionSelectionRow(row: ConditionSelectionRow) {
    if (readOnly) {
      return null;
    }
    if (
      row.conditionTypeName !== ConditionTypeName.Allergy &&
      row.conditionTypeName !== ConditionTypeName.FamilyHistory &&
      row.conditionTypeName !== ConditionTypeName.PhysiologyAndLifeFactors
    ) {
      return null;
    }
    const expanded = expandedRowIds.includes(row.id);
    return (
      <div
        className={cn('py-2')}
        data-row-id={row.id}
        key={row.id}
        style={{
          scrollMarginTop: '2.25rem',
        }}
      >
        <div className={'flex items-center'}>
          <Button
            onClick={() => setExpandedRowIds(expanded ? [] : [row.id].filter(Boolean))}
            variant="text"
            className="rounded-full"
            type="button"
            startIcon={<Icon icon={expanded ? 'times' : 'plus'} />}
          >
            {expanded ? 'Close' : 'Add New Feature'}
          </Button>
        </div>
        {expanded && (
          <ConditionSelectionPanel
            className={'mt-2'}
            conditionTypeName={row.conditionTypeName}
            onSave={async ({deselected, selected}) => {
              try {
                await updateBulkBiotwinCondition({
                  added: selected.map((condition) => ({
                    conditionId: condition.id,
                    userId,
                  })),
                  removedIds: deselected.map((condition) => condition.id),
                  userId,
                }).unwrap();
              } catch (e) {
                toastError(e);
              }
            }}
            onSelectGroup={() => {
              container.current?.querySelector(`[data-row-id="${row.id}"]`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }}
          />
        )}
      </div>
    );
  }
  function renderDetailsRow(row: DetailsRow) {
    return (
      <div
        className="py-2"
        data-row-id={row.id}
        key={row.id}
        style={{
          scrollMarginTop: '2.25rem',
        }}
      >
        <RelatedConditionsPanel
          className={'mb-4'}
          onSave={async ({deselectedIds, selectedIds}) => {
            try {
              await updateBulkBiotwinCondition({
                added: selectedIds.map((id) => ({
                  conditionId: id,
                  userId,
                })),
                removedIds: deselectedIds,
                userId,
              }).unwrap();
            } catch (e) {
              toastError(e);
            }
          }}
          onSelectGroup={() => {
            container.current?.querySelector(`[data-row-id="${row.id}"]`)?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }}
          relevantCondition={row.relevantCondition}
        />
      </div>
    );
  }
  function renderHeaderRow(row: HeaderRow) {
    const conditionTypeColor = getConditionTypeColorByName(row.conditionTypeName.trim()) ?? 'condition-other';
    return (
      <div
        className={`sticky top-0 flex items-center gap-4 pt-2 bg-white border-b border-${conditionTypeColor} z-2`}
        data-row-id={row.id}
        key={row.id}
      >
        <div
          className={`rounded-tr-lg sm:rounded-t-lg w-[13rem] py-1 px-4 text-${conditionTypeColor}-foreground bg-${conditionTypeColor}`}
        >
          {row.conditionTypeName}
        </div>
        <div className={'hidden whitespace-nowrap sm:flex sm:items-center'}>
          {row.conditionTypeName === ConditionTypeName.Diagnoses && (
            <span className={'inline-flex items-center gap-1 leading-none text-muted-foreground text-sm'}>
              <Icon icon={'info-circle'} /> Confirmed or suspected
            </span>
          )}
          {row.conditionTypeName === ConditionTypeName.Medications && (
            <span className={'inline-flex items-center gap-1 leading-none text-muted-foreground text-sm'}>
              <Icon icon={'info-circle'} /> Using now or planning to use
            </span>
          )}
          {row.conditionTypeName === ConditionTypeName.Procedures && (
            <span className={'absolute inline-flex items-center gap-1 leading-none text-muted-foreground text-sm'}>
              <Icon icon={'info-circle'} /> Add "perioperative" or "periprocedural" from{' '}
              <Button
                className={'p-0 text-sm'}
                onClick={() => setExpandedRowIds([`setting-selection-${careSettingRelevantCondition?.conditionId}`])}
                variant="link"
                type="button"
              >
                {careSettingRelevantCondition?.conditionName.toLocaleLowerCase()}
              </Button>{' '}
              if planned
            </span>
          )}
        </div>
      </div>
    );
  }
  function renderSettingSelectionRow(row: SettingSelectionRow) {
    if (readOnly) {
      return null;
    }
    const expanded = expandedRowIds.includes(row.id);
    const index = rows.indexOf(row);
    const nextRow = rows[index + 1];
    return (
      <div
        className={cn('border-zinc-300 py-2', nextRow != null && nextRow.type !== 'header' && 'border-b')}
        data-row-id={row.id}
        key={row.id}
        style={{
          scrollMarginTop: '2.25rem',
        }}
      >
        <div className={'flex items-center'}>
          <Button
            onClick={() => setExpandedRowIds(expanded ? [] : [row.id])}
            variant="text"
            className="rounded-full"
            type="button"
            startIcon={<Icon icon={expanded ? 'times' : 'plus'} />}
          >
            {expanded ? 'Close' : row.relevantCondition.conditionName}
          </Button>
        </div>
        {expanded && (
          <SettingSelectionPanel
            className={'mt-2'}
            onSave={async ({deselectedIds, selectedIds}) => {
              try {
                await updateBulkBiotwinCondition({
                  added: selectedIds.map((id) => ({
                    conditionId: id,
                    userId,
                  })),
                  removedIds: deselectedIds,
                  userId,
                }).unwrap();
              } catch (e) {
                toastError(e);
              }
            }}
            onSelectGroup={() => {
              container.current?.querySelector(`[data-row-id="${row.id}"]`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }}
            relevantCondition={row.relevantCondition}
          />
        )}
      </div>
    );
  }
  function addCondition(condition: Condition, optional = false) {
    getSubConditions({
      parentConditionId: condition.id,
    })
      .unwrap()
      .then((result) => {
        if (result.length > 0) {
          setLeafConditionSelectorDialogState({
            condition,
            optional,
            visible: true,
          });
        } else {
          void onAddCondition({
            conditionId: condition.id,
            drugId: condition.drugId ?? undefined,
          });
        }
      })
      .catch(console.error);
  }
  function isVisibleRelevantCondition(relevantCondition: RelevantCondition) {
    const phraseCount =
      biobookCounts?.conditionPhraseCounts.find(({conditionId}) => conditionId === relevantCondition.conditionId)
        ?.phraseCount ?? 0;
    if (relevantCondition.hideFromBiotype && session?.isAdmin !== 'true') {
      return false;
    }
    if (relevantCondition.hideWhenNoPhrase && phraseCount === 0) {
      return false;
    }
    return true;
  }
  function rowsFactory(): Row[] {
    const nonPatientSettingRootRelevantConditions = rootRelevantConditions.filter((rc) => !rc.isPatientSetting);
    const categoryRootRelevantConditions = nonPatientSettingRootRelevantConditions.filter(
      (rc) => rc.conditionTypeName === ConditionTypeName.Category,
    );
    const visibleCategoryRootRelevantConditions = categoryRootRelevantConditions.filter(isVisibleRelevantCondition);
    const nonCategoryRootRelevantConditions = nonPatientSettingRootRelevantConditions.filter(
      (rc) => rc.conditionTypeName !== ConditionTypeName.Category,
    );
    const visibleRootRelevantConditions = rootRelevantConditions.filter(isVisibleRelevantCondition);
    const patientSettingRows: Row[] =
      patientSettingRelevantConditions.length > 0
        ? [
            {
              conditionTypeName: 'Settings',
              id: settingsHeaderRowId,
              type: 'header',
            },
            ...patientSettingRelevantConditions.flatMap((ps) => {
              const settingSelectionRow: SettingSelectionRow = {
                relevantCondition: ps,
                id: `setting-selection-${ps.conditionId}`,
                type: 'setting-selection',
              };
              const settingRows = [
                ...rootRelevantConditions
                  .filter((rc) => !rc.isPatientSetting)
                  .filter((rc) => hasRelation(ps.conditionId, rc.conditionId))
                  .sort(compareRelevantConditions)
                  .flatMap((rc) => {
                    const conditionRow: ConditionRow = {
                      groupRowId: null,
                      headerRowId: settingsHeaderRowId,
                      id: `c-${rc.conditionId}`,
                      relevantCondition: rc,
                      type: 'condition',
                    };
                    const expanded = expandedRowIds.includes(conditionRow.id);
                    const detailsRow: DetailsRow = {
                      hidden: !expanded,
                      id: `details-${rc.conditionId}`,
                      relevantCondition: rc,
                      type: 'details',
                    };
                    const relatedConditionRows = visibleCategoryRootRelevantConditions
                      .filter((rc2) => hasRelation(rc.conditionId, rc2.conditionId))
                      .map((rc2) => {
                        const conditionRow2: ConditionRow = {
                          groupRowId: conditionRow.id,
                          headerRowId: settingsHeaderRowId,
                          hidden: !expanded,
                          id: `c-${rc2.conditionId}`,
                          relevantCondition: rc2,
                          type: 'condition',
                        };
                        return conditionRow2;
                      });
                    return [conditionRow, ...relatedConditionRows, detailsRow];
                  }),
              ];
              return [settingSelectionRow, ...settingRows];
            }),
          ]
        : [];
    const fixedGroups = readOnly
      ? {}
      : {
          [ConditionTypeName.Allergy]: [] as RelevantCondition[],
          [ConditionTypeName.FamilyHistory]: [] as RelevantCondition[],
          [ConditionTypeName.PhysiologyAndLifeFactors]: [] as RelevantCondition[],
        };
    const nonPatientSettingRows: Row[] = (
      Object.entries({
        ...fixedGroups,
        ...groupBy(
          visibleRootRelevantConditions.filter(
            (rc) =>
              rc.conditionTypeName !== ConditionTypeName.Category ||
              (nonCategoryRootRelevantConditions.every((rc2) => !hasRelation(rc2.conditionId, rc.conditionId)) &&
                patientSettingRelevantConditions.every((rc2) => !hasRelation(rc2.conditionId, rc.conditionId))),
          ),
          'conditionTypeName',
        ),
      }) as [ConditionTypeName, RelevantCondition[]][]
    )
      .sort((a, b) => compareRelevantConditionsByType(a[0], b[0]))
      .flatMap(([conditionTypeName, relevantConditionsDeduplicated]) => {
        const headerRow: HeaderRow = {
          conditionTypeName,
          id: `header-${conditionTypeName}`,
          type: 'header',
        };
        const dataRows = relevantConditionsDeduplicated.sort(compareRelevantConditions).flatMap((relevantCondition) => {
          const conditionRow: ConditionRow = {
            groupRowId: null,
            headerRowId: headerRow.id,
            id: `c-${relevantCondition.conditionId}`,
            relevantCondition,
            type: 'condition',
          };
          const expanded = expandedRowIds.includes(conditionRow.id);
          const detailsRow: DetailsRow = {
            hidden: !expanded,
            id: `details-${relevantCondition.conditionId}`,
            relevantCondition,
            type: 'details',
          };
          const relatedConditionRows: ConditionRow[] =
            conditionTypeName === ConditionTypeName.Category
              ? []
              : visibleCategoryRootRelevantConditions
                  .filter((rc) => hasRelation(relevantCondition.conditionId, rc.conditionId))
                  .map((rc) => {
                    const conditionRow2: ConditionRow = {
                      groupRowId: conditionRow.id,
                      headerRowId: headerRow.id,
                      hidden: !expanded,
                      id: `c-${rc.conditionId}`,
                      relevantCondition: rc,
                      type: 'condition',
                    };
                    return conditionRow2;
                  });
          return [conditionRow, ...relatedConditionRows, detailsRow];
        });
        const conditionSelectionRow: ConditionSelectionRow = {
          conditionTypeName,
          id: `condition-selection-${conditionTypeName}`,
          type: 'condition-selection',
        };
        return [headerRow, ...dataRows, conditionSelectionRow];
      }) as Row[];
    return [...patientSettingRows, ...nonPatientSettingRows]
      .filter(
        (row) =>
          row.type !== 'condition' ||
          row.relevantCondition.conditionRelationType !== ConditionRelationType.RequiredRelated ||
          !row.relevantCondition.isGroup ||
          !row.relevantCondition.isScoreBased,
      )
      .filter((row, i, a) => a.findIndex((r) => r.id === row.id) === i);
  }
  async function onAddCondition({conditionId, drugId, value}: {conditionId: number; drugId?: number; value?: number}) {
    if (session?.id != null) {
      try {
        const willBeRemovedRelevantConditions = relevantConditionsDeduplicated?.filter(
          (rc, _index, array) =>
            rc.conditionRelationType === ConditionRelationType.Selected &&
            (rc.conditionId === conditionId ||
              array.some(
                (rc2) =>
                  rc2.conditionId === conditionId &&
                  rc2.conditionRelationType === ConditionRelationType.Parent &&
                  rc2.relatedConditionIds?.includes(rc.conditionId),
              )),
        );
        if (!willBeRemovedRelevantConditions?.length) {
          await createBiotwinCondition({
            userId,
            conditionId,
            drugId,
            value,
          }).unwrap();
        } else {
          await updateBulkBiotwinCondition({
            userId,
            added: [
              {
                conditionId,
                drugId,
                value,
              },
            ],
            removedIds: willBeRemovedRelevantConditions.map((c) => c.conditionId),
          }).unwrap();
        }
      } catch (e) {
        if (isDemo && isFetchBaseQueryError(e) && isApiResponseBodyError(e.data) && e.data.statusCode === '0140') {
          showLimitReachedDialog({
            message: 'Register right away to create a full biotwin.',
            warning: "You've reached the limit for adding features.",
          });
        } else {
          toastError(e);
        }
      }
    }
  }
  async function onRemoveCondition({conditionId}: {conditionId: number}) {
    const biotwinCondition = biotwinConditions?.find(
      (biotwinCondition) => biotwinCondition.conditionId === conditionId,
    );
    if (biotwinCondition != null) {
      if (biotwinCondition.note) {
        confirm({
          onConfirm: () => removeCondition(biotwinCondition),
          confirmClassName: 'rounded-full',
          title: 'Confirm',
          message: 'Feature has note. Do you want to remove it anyway?',
          cancelClassName: 'rounded-full bg-transparent',
        });
      } else {
        await removeCondition(biotwinCondition);
      }
    }
  }
  async function removeCondition(biotwinCondition: BiotwinCondition) {
    try {
      await deleteBiotwinCondition({
        id: biotwinCondition.id,
        userId,
      });
    } catch (e) {
      toastError(e);
    }
  }
  function stepsNewConditionFactory(): Step[] {
    const steps: Step[] = [];
    steps.push({
      element: '[data-name="biotwin-condition-expander"]',
      intro: (
        <>
          <p>This mark refers that this feature has the details.</p>{' '}
          <p>Please check and add/update the details to get correct information.</p>
        </>
      ),
      position: 'bottom',
      title: 'This Feature Needs The Details',
    });
    steps.push({
      element: '[data-name="biotwin-condition-phrase-count-link"]',
      intro: 'You can view pages specific for this feature by clicking here.',
      position: 'bottom',
      title: 'Page Count',
    });
    if (!readOnly) {
      steps.push({
        element: '[data-name="biotwin-condition-operations"]',
        intro: 'You can add note to the feature or remove it by using these actions.',
        position: 'bottom',
        title: 'Feature Operations',
      });
    }
    return steps;
  }
};
const steps: Step[] = [
  {
    element: '[data-name="condition-search-bar-input"]',
    intro: 'The more a biotwin is detailed, the more precise information is included in its biobook.',
    position: 'bottom',
    title: 'Construct & Update Biotwin',
  },
  {
    element: '[data-name="condition-search-bar-dropdown"]',
    intro: 'Narrow your search by selecting a type at first.',
    position: 'bottom',
    title: 'Types of the Features',
  },
];
